import { SUPABASE_CONFIG, getAuthHeaders, buildApiUrl } from '../config/supabase';

// Types
export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  title: string;
  description: string;
  short_description?: string;
  category_id: string;
  image_url?: string;
  technologies?: string[];
  features?: string[];
  client?: string;
  duration?: string;
  budget?: string;
  status: string;
  project_url?: string;
  github_url?: string;
  demo_url?: string;
  created_at: string;
  updated_at: string;
  category?: Category;
}

// API functions
export const supabaseApi = {
  // Categories
  async getCategories(): Promise<Category[]> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.CATEGORIES, 'select=*'), {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  async createCategory(category: Omit<Category, 'id' | 'created_at' | 'updated_at'>): Promise<Category | null> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.CATEGORIES), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(category),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create category');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating category:', error);
      return null;
    }
  },

  async updateCategory(id: string, category: Partial<Category>): Promise<Category | null> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.CATEGORIES, `id=eq.${id}`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(category),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update category');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating category:', error);
      return null;
    }
  },

  async deleteCategory(id: string): Promise<boolean> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.CATEGORIES, `id=eq.${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error deleting category:', error);
      return false;
    }
  },

  // Projects
  async getProjects(): Promise<Project[]> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.PROJECTS, 'select=*,category:categories(*)'), {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching projects:', error);
      return [];
    }
  },

  async createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project | null> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.PROJECTS), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(project),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create project');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating project:', error);
      return null;
    }
  },

  async updateProject(id: string, project: Partial<Project>): Promise<Project | null> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.PROJECTS, `id=eq.${id}`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(project),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating project:', error);
      return null;
    }
  },

  async deleteProject(id: string): Promise<boolean> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.PROJECTS, `id=eq.${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error deleting project:', error);
      return false;
    }
  },

  async getProject(id: string): Promise<Project | null> {
    try {
      const response = await fetch(buildApiUrl(SUPABASE_CONFIG.API_ENDPOINTS.PROJECTS, `id=eq.${id}&select=*,category:categories(*)`), {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch project');
      }
      
      const projects = await response.json();
      return projects[0] || null;
    } catch (error) {
      console.error('Error fetching project:', error);
      return null;
    }
  },
};
